import { useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useGetCategories, useCreateProduct } from "../backend/hooks";
import { toast } from "react-toastify";
import ReactQuill from "react-quill-new";
import { normalizeRichText } from "../utils/richText";
import "react-quill-new/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import ImageManager from "./imageManager";

// Validation schema
const productValidationSchema = Yup.object({
  name: Yup.string()
    .required("Product name is required")
    .min(3, "Name must be at least 3 characters")
    .max(100, "Name cannot exceed 100 characters"),
  categoryId: Yup.string().required("Please select a category"),
  description: Yup.string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters"),
  technicalDescription: Yup.string().nullable(),
  price: Yup.number().when("variants", {
    is: (variants) => !variants || variants.length === 0,
    then: (schema) =>
      schema
        .required("Price is required")
        .positive("Price must be positive")
        .typeError("Price must be a number"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  inStock: Yup.boolean(),
  priceUsd: Yup.number().typeError("USD price must be a number").min(0, "Cannot be negative").nullable(),
  shippingPkr: Yup.number().typeError("Shipping must be a number").min(0, "Cannot be negative").nullable(),
  shippingUsd: Yup.number().typeError("Shipping must be a number").min(0, "Cannot be negative").nullable(),
  variants: Yup.array().of(
    Yup.object({
      name: Yup.string().trim().required("Option name is required"),
      price: Yup.number()
        .typeError("Price must be a number")
        .min(0, "Price cannot be negative")
        .required("Price is required"),
      price_usd: Yup.number().typeError("USD price must be a number").min(0, "Cannot be negative").nullable(),
      in_stock: Yup.string(),
    })
  ),
  tags: Yup.string(),
});

export default function AddProduct() {
  const { data: categoriesData } = useGetCategories();
  const { mutate: createProduct, isPending: isProductLoading } = useCreateProduct();
  const navigate = useNavigate();

  // Any number of images; the first one is the product's banner image
  const [images, setImages] = useState([]);

  // Named options ("Standard Quality", "Large", ...) each with their own price.
  // formik.values is captured per render, so two row edits in the same tick would
  // both build on the same stale array. The ref advances immediately, keeping
  // rapid clicks correct.
  const variantsRef = useRef([]);
  const nextVariantId = useRef(0);
  const setVariants = (next) => {
    variantsRef.current = next;
    formik.setFieldValue("variants", next);
  };

  const addVariant = () =>
    setVariants([
      ...variantsRef.current,
      { _id: `opt-${nextVariantId.current++}`, name: "", price: "", price_usd: "", in_stock: "yes" },
    ]);

  const updateVariant = (id, field, value) =>
    setVariants(
      variantsRef.current.map((variant) =>
        variant._id === id ? { ...variant, [field]: value } : variant
      )
    );

  const removeVariant = (id) =>
    setVariants(variantsRef.current.filter((variant) => variant._id !== id));

  // Row-level errors only make sense once the admin has tried to submit
  const variantError = (index, field) => {
    if (!formik.submitCount) return null;
    const rowErrors = Array.isArray(formik.errors.variants) ? formik.errors.variants[index] : null;
    return rowErrors && rowErrors[field] ? rowErrors[field] : null;
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      categoryId: "",
      description: "",
      technicalDescription: "",
      price: "",
      inStock: true,
      priceUsd: "",
      shippingPkr: "",
      shippingUsd: "",
      variants: [],
      tags: "",
    },
    validationSchema: productValidationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const productData = new FormData();
      productData.append("title", values.name);
      productData.append("category_id", values.categoryId);
      productData.append("description", values.description);
      productData.append("technical_description", normalizeRichText(values.technicalDescription));
      const options = values.variants
        .filter((variant) => variant.name.trim() !== "")
        .map((variant) => ({
          name: variant.name.trim(),
          price: Number(variant.price),
          price_usd: variant.price_usd === "" || variant.price_usd === undefined ? null : Number(variant.price_usd),
          in_stock: variant.in_stock === "no" ? "no" : "yes",
        }));

      // With options the single price field is hidden, so the product price
      // tracks the first option - the one the storefront preselects.
      productData.append("price", options.length ? options[0].price : values.price);
      productData.append("price_usd", options.length ? (options[0].price_usd ?? "") : values.priceUsd);
      productData.append("shipping_pkr", values.shippingPkr);
      productData.append("shipping_usd", values.shippingUsd);
      productData.append("in_stock", values.inStock);
      productData.append("tags", values.tags);
      // Options are sent as JSON; an empty list means the product sells at the
      // single price above.
      productData.append("variants", JSON.stringify(options));

      // Sent in display order, so the server stores the banner first
      images.forEach((image) => {
        productData.append("images", image.file);
      });

      setSubmitting(true);

      createProduct(productData, {
        onSuccess: (response) => {
          toast.success(response?.message || "Product created successfully!");
          resetForm();
          setImages([]);
          setSubmitting(false);
          navigate('/dashboard/product');
        },
        onError: (error) => {
          const errorMsg = error?.response?.data?.message || error.message || "Failed to create product";
          toast.error(errorMsg);
          setSubmitting(false);
        },
      });
    },
  });

  variantsRef.current = formik.values.variants;
  const hasOptions = formik.values.variants.length > 0;

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["clean"],
    ],
  };
  const quillFormats = ["header", "bold", "italic", "underline", "list"];

  return (
    <main className="flex-1 flex flex-col min-h-screen">
      <form onSubmit={formik.handleSubmit} className="py-4 max-w-7xl mx-auto w-full grid grid-cols-12 gap-8">
        {/* Left Column */}
        <div className="col-span-12 md:col-span-8 space-y-8">
          {/* Identity Section */}
          <section className="bg-surface-container-lowest p-8 rounded-xl space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-on-surface-variant">
                Identity
              </span>
              <div className="h-[1px] flex-1 bg-surface-container"></div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="col-span-2">
                <label className="block text-sm font-bold text-on-surface mb-2">Product Name</label>
                <input
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full bg-surface-container-highest border-none rounded-lg p-3 text-on-surface focus:ring-2 focus:ring-primary transition-all ${formik.touched.name && formik.errors.name ? "ring-2 ring-error" : ""
                    }`}
                  placeholder="e.g. Weighted Sensory Compression Vest"
                  type="text"
                />
                {formik.touched.name && formik.errors.name && (
                  <p className="text-error text-xs mt-1">{formik.errors.name}</p>
                )}
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-bold text-on-surface mb-2">Category</label>
                <select
                  name="categoryId"
                  value={formik.values.categoryId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full bg-surface-container-highest border-none rounded-lg p-3 text-on-surface focus:ring-2 focus:ring-primary transition-all appearance-none ${formik.touched.categoryId && formik.errors.categoryId ? "ring-2 ring-error" : ""
                    }`}
                >
                  <option value="">Select category</option>
                  {categoriesData?.data?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {formik.touched.categoryId && formik.errors.categoryId && (
                  <p className="text-error text-xs mt-1">{formik.errors.categoryId}</p>
                )}
              </div>
            </div>
          </section>

          {/* Clinical Data Section */}
          <section className="bg-surface-container-lowest p-8 rounded-xl space-y-6">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-on-surface-variant">
                Clinical Data
              </span>
              <div className="h-[1px] flex-1 bg-surface-container"></div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-on-surface mb-2">Description</label>
                <textarea
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full bg-surface-container-highest border-none rounded-lg p-3 text-on-surface focus:ring-2 focus:ring-primary transition-all ${formik.touched.description && formik.errors.description ? "ring-2 ring-error" : ""
                    } h-40 md:h-24`}
                  placeholder="Detail the therapeutic benefits, pressure mapping, and patient use cases..."
                />
                {formik.touched.description && formik.errors.description && (
                  <p className="text-error text-xs mt-1">{formik.errors.description}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-bold text-on-surface mb-2">Technical Description</label>
                <div className="min-h-[200px] md:min-h-[120px]">
                  <ReactQuill
                    theme="snow"
                    value={formik.values.technicalDescription}
                    onChange={(content) => formik.setFieldValue("technicalDescription", content)}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter ISO certifications, material safety data, weight limits, etc."
                    className="bg-surface-container-highest rounded-lg h-full"
                  />
                </div>
                <p className="text-xs text-on-surface-variant mt-1">
                  Tip: Use bold, lists, and headings to structure the technical description.
                </p>
              </div>
              <div>
                <label className="block text-sm font-bold text-on-surface mb-2">Tags (comma separated)</label>
                <input
                  name="tags"
                  value={formik.values.tags}
                  onChange={formik.handleChange}
                  className="w-full bg-surface-container-highest border-none rounded-lg p-3 text-on-surface focus:ring-2 focus:ring-primary transition-all"
                  placeholder="e.g., sensory, weighted, compression"
                  type="text"
                />
                <p className="text-xs text-on-surface-variant mt-1">Helps with search and filtering</p>
              </div>
            </div>
          </section>

          {/* Media Assets Section */}
          <section className="bg-surface-container-lowest p-8 rounded-xl space-y-6">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-on-surface-variant">
                Media Assets
              </span>
              <div className="h-[1px] flex-1 bg-surface-container"></div>
            </div>
            <ImageManager value={images} onChange={setImages} />
          </section>
        </div>

        {/* Right Sidebar */}
        <div className="col-span-12 md:col-span-4 space-y-8">
          <div className="bg-surface-container-lowest p-6 rounded-xl shadow-sm space-y-6">
            <h3 className="text-lg font-bold text-sky-900">Inventory Status</h3>
            <div className="space-y-4">
              {hasOptions ? (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                    Clinical Price (PKR)
                  </label>
                  <div className="bg-surface-container-highest rounded-lg p-3 flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary text-lg">tune</span>
                    <span className="text-sm font-bold text-on-surface">
                      Set per option below
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                    Customers pay the price of the option they pick. The first option
                    is the one shown by default.
                  </p>
                </div>
              ) : (
                <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Price (PKR)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">Rs</span>
                      <input
                        name="price"
                        value={formik.values.price}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full pl-8 bg-surface-container-highest border-none rounded-lg p-3 text-on-surface font-bold ${formik.touched.price && formik.errors.price ? "ring-2 ring-error" : ""
                          }`}
                        placeholder="0.00"
                        type="number"
                        step="0.01"
                      />
                    </div>
                    {formik.touched.price && formik.errors.price && (
                      <p className="text-error text-xs mt-1">{formik.errors.price}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Price (USD)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                      <input
                        name="priceUsd"
                        value={formik.values.priceUsd}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full pl-7 bg-surface-container-highest border-none rounded-lg p-3 text-on-surface font-bold ${formik.touched.priceUsd && formik.errors.priceUsd ? "ring-2 ring-error" : ""
                          }`}
                        placeholder="Optional"
                        type="number"
                        step="0.01"
                      />
                    </div>
                    {formik.touched.priceUsd && formik.errors.priceUsd && (
                      <p className="text-error text-xs mt-1">{formik.errors.priceUsd}</p>
                    )}
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 -mt-2">
                  Leave USD blank to show Rs to international visitors too.
                </p>
                </>
              )}
              <div className="pt-4 border-t border-outline-variant/20">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Options / Qualities
                  </label>
                  <button
                    type="button"
                    onClick={addVariant}
                    className="text-xs font-bold text-primary hover:opacity-70 flex items-center gap-1"
                  >
                    <span className="material-symbols-outlined text-sm">add</span>
                    Add option
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mb-3 leading-snug">
                  Leave empty to sell at the single price above. Add options such as
                  "Standard Quality" or "Large" when the product comes in more than one
                  grade &ndash; each option carries its own price.
                </p>

                {formik.values.variants.length === 0 && (
                  <p className="text-xs text-slate-400 italic">No options &ndash; single price product.</p>
                )}

                <div className="space-y-3">
                  {formik.values.variants.map((variant, index) => (
                    <div key={variant._id} className="bg-surface-container-highest rounded-lg p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          value={variant.name}
                          onChange={(e) => updateVariant(variant._id, "name", e.target.value)}
                          className="flex-1 min-w-0 bg-surface-container-lowest border-none rounded-md p-2 text-sm text-on-surface"
                          placeholder="Option name, e.g. Premium Quality"
                          type="text"
                        />
                        <button
                          type="button"
                          onClick={() => removeVariant(variant._id)}
                          className="text-error hover:opacity-70 shrink-0"
                          aria-label={"Remove option " + (index + 1)}
                        >
                          <span className="material-symbols-outlined text-lg">delete</span>
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1 min-w-0">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">
                            Rs
                          </span>
                          <input
                            value={variant.price}
                            onChange={(e) => updateVariant(variant._id, "price", e.target.value)}
                            className="w-full pl-7 bg-surface-container-lowest border-none rounded-md p-2 text-sm text-on-surface font-bold"
                            placeholder="0.00"
                            type="number"
                            step="0.01"
                          />
                        </div>
                        <div className="relative flex-1 min-w-0">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">
                            $
                          </span>
                          <input
                            value={variant.price_usd ?? ""}
                            onChange={(e) => updateVariant(variant._id, "price_usd", e.target.value)}
                            className="w-full pl-6 bg-surface-container-lowest border-none rounded-md p-2 text-sm text-on-surface font-bold"
                            placeholder="USD"
                            type="number"
                            step="0.01"
                          />
                        </div>
                        <select
                          value={variant.in_stock}
                          onChange={(e) => updateVariant(variant._id, "in_stock", e.target.value)}
                          className="bg-surface-container-lowest border-none rounded-md p-2 text-sm text-on-surface shrink-0"
                        >
                          <option value="yes">In stock</option>
                          <option value="no">Sold out</option>
                        </select>
                      </div>
                      {(variantError(index, "name") || variantError(index, "price")) && (
                        <p className="text-error text-[11px]">
                          {variantError(index, "name") || variantError(index, "price")}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-outline-variant/20 space-y-2">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Delivery Charge
                </label>
                <p className="text-[11px] text-slate-500 -mt-1 leading-snug">
                  Charged once per order for this product. Leave blank for free delivery.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">Rs</span>
                    <input
                      name="shippingPkr"
                      value={formik.values.shippingPkr}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="w-full pl-8 bg-surface-container-highest border-none rounded-lg p-3 text-sm text-on-surface font-bold"
                      placeholder="0.00"
                      type="number"
                      step="0.01"
                    />
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">$</span>
                    <input
                      name="shippingUsd"
                      value={formik.values.shippingUsd}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="w-full pl-7 bg-surface-container-highest border-none rounded-lg p-3 text-sm text-on-surface font-bold"
                      placeholder="0.00"
                      type="number"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <span className="text-sm font-medium">In Stock</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    name="inStock"
                    type="checkbox"
                    className="sr-only peer"
                    checked={formik.values.inStock}
                    onChange={formik.handleChange}
                  />
                  <div className="w-11 h-6 bg-surface-container-highest peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Catalog Preview */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-on-surface-variant">
                Catalog Preview
              </span>
              <div className="h-[1px] flex-1 bg-surface-container"></div>
            </div>
            <div className="glass-card rounded-2xl overflow-hidden shadow-xl border border-white/40">
              <div className="aspect-square bg-slate-200 overflow-hidden relative group">
                {images[0] ? (
                  <img className="w-full h-full object-cover" src={images[0].url} alt="Product preview" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <span className="material-symbols-outlined text-5xl">image</span>
                  </div>
                )}
                <div className="absolute top-4 right-4 px-3 py-1 bg-secondary-fixed text-on-secondary-fixed-variant text-[10px] font-bold rounded-full uppercase">
                  {formik.values.inStock ? "In Stock" : "Out of Stock"}
                </div>
              </div>
              <div className="p-5 space-y-2">
                <div className="flex justify-between items-start">
                  <h4 className="text-sm font-bold text-on-surface leading-tight">
                    {formik.values.name || "Product Name"}
                  </h4>
                  <span className="text-primary font-bold">
                    Rs {hasOptions ? formik.values.variants[0].price || "0.00" : formik.values.price || "0.00"}
                  </span>
                </div>
                <p className="text-xs text-slate-500 line-clamp-2">
                  {formik.values.description || "Product description will appear here."}
                </p>
                {formik.values.tags && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {formik.values.tags.split(",").map((tag, i) => (
                      <span
                        key={i}
                        className="text-[10px] bg-surface-container px-2 py-0.5 rounded-full text-on-surface-variant"
                      >
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={formik.isSubmitting || isProductLoading}
            className="w-full bg-primary text-on-primary py-3 rounded-xl font-bold shadow-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {formik.isSubmitting || isProductLoading ? "Creating..." : "Create Product"}
          </button>
        </div>
      </form>
    </main>
  );
}